import { useAtomValue } from 'jotai';
import { useFormContext } from 'react-hook-form';

import { Grid, Stack } from '@crossnokaye/ux-common';

import { DisplayRefDisplayType } from '@repo/sdk/models/components';

import { EmptyStatePlaceholder } from '@/components/molecules/empty-state-placeholder';
import { settingsInputMap } from '@/features/equipment-browser/equipment-details/settings/maps/settings-inputs.map';
import { equipmentLoadingAtom } from '@/features/equipment-browser/equipment-details/state/equipment-response.state';
import { SubSectionTemplate } from '@/features/equipment-browser/equipment-details/templates/subsection-template';
import { EnhancedSetting } from '@/features/equipment-browser/subscriptions/use-get-settings-values';

import { SettingFieldButton } from './setting-field-button';

interface GeneralSettingsProps {
  settings: EnhancedSetting[];
}

export const GeneralSettings = ({ settings }: GeneralSettingsProps): React.ReactNode => {
  const form = useFormContext();
  const isLoading = useAtomValue(equipmentLoadingAtom);

  // split settings to pull all buttons out to a separate array
  const buttons = settings.filter(
    (setting: EnhancedSetting) => setting.displayType === DisplayRefDisplayType.Button
  );
  const otherSettings = settings.filter(
    (setting: EnhancedSetting) => setting.displayType !== DisplayRefDisplayType.Button
  );

  const hasNoSettings = otherSettings.length === 0 && buttons.length === 0;

  return (
    <SubSectionTemplate title={'General'} isLoading={isLoading}>
      {hasNoSettings && <EmptyStatePlaceholder message="No settings available" />}

      {otherSettings?.length > 0 && (
        <Grid variant="responsive-5" className="p-gutter-sm @xl:p-gutter">
          {otherSettings?.map((setting: EnhancedSetting) => {
            const SettingInput =
              settingsInputMap[setting.displayType ?? DisplayRefDisplayType.Input];

            return (
              <Stack key={`input-${setting.ref.id}`} gap="sm">
                <SettingInput form={form} setting={setting} name={`settings.${setting.ref.id}`} />
              </Stack>
            );
          })}
        </Grid>
      )}

      {buttons?.length > 0 && (
        <Grid variant="responsive-5" className="p-gutter-sm @xl:p-gutter">
          {buttons?.map((button: EnhancedSetting) => (
            <SettingFieldButton key={`setting-button-${button.ref.id}`} setting={button} />
          ))}
        </Grid>
      )}
    </SubSectionTemplate>
  );
};
